/**
 * SubViews: Renders the View wrapper (context/URL sync) and the active page
 * based on route param :id (e.g. overview, strategymap, kpis, mytasks).
 * Pages are lazy-loaded to reduce initial bundle size.
 */
import React, { Suspense } from "react";
import { useParams } from "react-router-dom";
import View from "qpr-base/app/Views";
import Loader from "qpr-base/ui/components/Loader";

import PageNotFound from "pages/PageNotFound";

const Overview = React.lazy(() => import("pages/overview/index"));
const StrategyMap = React.lazy(() => import("pages/strategymap/index"));
const KPIs = React.lazy(() => import("pages/kpis/index"));
const KPIsAndInitiatives = React.lazy(() => import("pages/kpisandinitiatives/index"));
const KPIDetails = React.lazy(() => import("pages/kpidetails/index"));
const InitiativeDetails = React.lazy(() => import("pages/initiativedetails/index"));
const ExecutiveDashboard = React.lazy(() => import("pages/executivedashboard/index"));
const CascadePage = React.lazy(() => import("pages/cascade/index"));
const ReportTemplates = React.lazy(() => import("pages/reporttemplates/index"));
const ConfigurableReports = React.lazy(() => import("pages/configurablereports/index"));
const MyTasks = React.lazy(() => import("pages/mytasks/index"));
const TaskWorkflow = React.lazy(() => import("pages/taskworkflow/index"));
const WorkflowInput = React.lazy(() => import("pages/workflowinput/index"));
const WorkflowAdmin = React.lazy(() => import("pages/workflowadmin/index"));
const AdminPanel = React.lazy(() => import("pages/adminpanel/index"));
const AdminTools = React.lazy(() => import("pages/admintools/index"));
const Division = React.lazy(() => import("pages/division/index"));
const Objective = React.lazy(() => import("pages/objective/index"));
const Perspective = React.lazy(() => import("pages/perspective/index"));
const ElementTest = React.lazy(() => import("pages/elementtest/index"));
const Initiatives = React.lazy(() => import("pages/initiatives/index"));
const DocsPage = React.lazy(() => import("pages/docs/index"));
const FilesAdminPage = React.lazy(() => import("pages/files/index"));
const CommentsAdminPage = React.lazy(() => import("pages/comments/index"));
const KpiRequirementsPage = React.lazy(() => import("pages/kpirequirements/index"));
const KpiLogsPage = React.lazy(() => import("pages/kpilogs/index"));
const KPIsAndProjectsManagement = React.lazy(() => import("pages/kpisandprojectsmanagement/index"));
const CompletedTasks = React.lazy(() => import("pages/completedtasks/index"));
const Delegation = React.lazy(() => import("pages/delegation/index"));
const UpdateUsers = React.lazy(() => import("pages/updateusers/index"));
const UserManagement = React.lazy(() => import("pages/usermanagement/index"));
const WorkflowPattern = React.lazy(() => import("pages/workflowpattern/index"));
const DivisionCycleHistory = React.lazy(
  () => import("pages/divisioncyclehistory/index")
);
const UpdateKpis = React.lazy(() => import("pages/updatekpis/index"));
const KpiSettings = React.lazy(() => import("pages/kpisettings/index"));
const TdfStructure = React.lazy(() => import("pages/tdfstructure/index"));
const SystemLogs = React.lazy(() => import("pages/systemlogs/index"));
const SystemSettings = React.lazy(() => import("pages/systemsettings/index"));
const KpiNotes = React.lazy(() => import("pages/kpinotes/index"));
const CeoNotes = React.lazy(() => import("pages/ceonotes/index"));
const CalculationCenter = React.lazy(() => import("pages/calculationcenter/index"));
const LandingPage = React.lazy(() => import("pages/landingpage/index"));

const pageToComponent: Record<string, React.ComponentType<any>> = {
  overview: Overview,
  strategymap: StrategyMap,
  kpis: KPIs,
  kpisandinitiatives: KPIsAndInitiatives,
  kpidetails: KPIDetails,
  initiativedetails: InitiativeDetails,
  executivedashboard: ExecutiveDashboard,
  cascade: CascadePage,
  reporttemplates: ReportTemplates,
  configurablereports: ConfigurableReports,
  mytasks: MyTasks,
  taskworkflow: TaskWorkflow,
  workflowinput: WorkflowInput,
  workflowadmin: WorkflowAdmin,
  adminpanel: AdminPanel,
  admintools: AdminTools,
  division: Division,
  objective: Objective,
  perspective: Perspective,
  elementtest: ElementTest,
  initiatives: Initiatives,
  docs: DocsPage,
  files: FilesAdminPage,
  comments: CommentsAdminPage,
  kpirequirements: KpiRequirementsPage,
  kpilogs: KpiLogsPage,
  kpisandprojectsmanagement: KPIsAndProjectsManagement,
  completedtasks: CompletedTasks,
  mycompletedtasks: CompletedTasks,
  delegation: Delegation,
  updateusers: UpdateUsers,
  usermanagement: UserManagement,
  workflowpattern: WorkflowPattern,
  divisioncyclehistory: DivisionCycleHistory,
  updatekpis: UpdateKpis,
  kpisettings: KpiSettings,
  tdfstructure: TdfStructure,
  systemlogs: SystemLogs,
  systemsettings: SystemSettings,
  kpinotes: KpiNotes,
  ceonotes: CeoNotes,
  calculationcenter: CalculationCenter,
  landingpage: LandingPage,
};

const SubViews = () => {
  const params = useParams<{ id?: string }>();
  const pageId = params.id || "";
  const PageComponent = pageToComponent[pageId] || PageNotFound;

  return (
    <View>
      <Suspense fallback={<Loader />}>
        <PageComponent />
      </Suspense>
    </View>
  );
};

export default SubViews;
